'use client';

import { getClassNames } from '@websolutespa/bom-core';
import { useCurrency, useLabel } from '@websolutespa/bom-mixer-hooks';
import { IconLlmChevronRightCircle } from '@websolutespa/bom-mixer-icons';
import { ReactNode } from 'react';
import { slugify } from 'transliteration';
import { LlmBlock, LlmChunkProduct, LlmChunkProductItem } from '../../../types';

export const ProductWrapper = ({ children, ...item }: (LlmChunkProductItem | LlmChunkProduct) & { children: ReactNode }) => {
  // const label = useLabel();
  const classNames = getClassNames('llm__product', `llm__product--${item.productType}`);
  return item.url ? (
    <a className={classNames} href={item.url} target="_blank" rel="noreferrer" aria-label={item.title}>
      {children}
    </a>
  ) : (
    <div className={classNames}>
      {children}
    </div>
  );
};

const limitAvailability = false;

export const Product: React.FC<LlmBlock<LlmChunkProductItem | LlmChunkProduct>> = (props) => {
  const { item } = props;
  const label = useLabel();
  const currency = useCurrency();
  function asNumber(value: string | number | undefined): number {
    return value ? Number(value) : 0;
  }
  const price = asNumber(item.price);
  const hasPriceLabel = item.priceLabel != null;
  const hasPrice = item.price != null && !hasPriceLabel;
  const fullPrice = asNumber(item.fullPrice) || price;
  const hasFullPriceLabel = item.fullPriceLabel != null;
  const hasFullPrice = fullPrice != null && !hasFullPriceLabel;
  const availability = asNumber(item.availability);
  const hasAvailabilityLabel = item.availabilityLabel != null;
  const hasAvailability = item.availability != null && !hasAvailabilityLabel;
  const labelKey = item.label ? `-${slugify(item.label)}` : '';
  return (
    <ProductWrapper {...item}>
      {item.media.type === 'image' && (
        <div className="llm__product-media">
          <div className="llm__product-asset">
            <img src={item.media.src} alt="" />
          </div>
          {item.label && (
            <div
              className="llm__product-label"
              style={{
                background: `var(--llm-product-label${labelKey}-background, var(--llm-product-label-background))`,
                color: `var(--llm-product-label${labelKey}-foreground, var(--llm-product-label-foreground))`,
              }}
            >{item.label}</div>
          )}
        </div>
      )}
      <div className="llm__product-content">
        {item.sku && (
          <div className="llm__product-sku">{item.sku}</div>
        )}
        <div className="llm__product-grow">
          {item.title && (
            <div className="llm__product-title" dangerouslySetInnerHTML={{ __html: item.title }} />
          )}
          {item.abstract && (
            <div className="llm__product-abstract" dangerouslySetInnerHTML={{ __html: item.abstract }} />
          )}
        </div>
        {hasPrice && (
          <div className="llm__product-prices">
            <div className="llm__product-price">{currency(price)}</div>
            {fullPrice > price && (
              <div className="llm__product-fullprice">{hasFullPriceLabel ? item.fullPriceLabel : currency(fullPrice)}</div>
            )}
          </div>
        )}
        {hasPriceLabel && (
          <div className="llm__product-prices">
            <div className="llm__product-price">{item.priceLabel}</div>
            {fullPrice > price && (
              <div className="llm__product-fullprice">{hasFullPriceLabel ? item.fullPriceLabel : currency(fullPrice)}</div>
            )}
          </div>
        )}
        {hasAvailability && (
          availability ? (
            limitAvailability ? (
              availability > 100 ? (
                <div className="llm__product-available">{label('llm.available')}</div>
              ) : (
                <div className="llm__product-available">{label('llm.availability', { availability: availability })}</div>
              )
            ) : (
              <div className="llm__product-available">{label('llm.availability', { availability: availability })}</div>
            )
          ) : (
            <div className="llm__product-soldout">{label('llm.soldOut')}</div>
          )
        )}
        {hasAvailabilityLabel && (
          availability ? (
            <div className="llm__product-available">{item.availabilityLabel}</div>
          ) : (
            <div className="llm__product-soldout">{item.availabilityLabel}</div>
          )
        )}
        {item.cta && (
          <div className="llm__product-cta">
            <span>{item.cta}</span> <IconLlmChevronRightCircle />
          </div>
        )}
      </div>
    </ProductWrapper>
  );
};
